import { useState, useRef, useEffect } from 'react';

const HERO_IMG =
  'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/610009ab-605b-40b3-a303-4e68d624ef31/clean-shot-2026-03-08-at-16-49-38-2-x-1920w.webp';
const PROJECT_IMG =
  'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/a4a2d395-9a89-4879-bea2-ce0ec1c6b479/clean-shot-2026-03-08-at-16-51-19-2-x-1920w.webp';

const WA_LINK =
  'https://wa.me/6593651286?text=Hi%2C%20I%20am%20interested%20in%20the%20Vela%20Bay%20project';

function HotlineDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-[#172C37] text-white font-bold px-6 py-3 rounded-full hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap text-sm text-center flex items-center justify-center gap-2"
      >
        <span>SHOWFLAT HOTLINE: +65 9365 1286</span>
        <i className={`ri-arrow-down-s-line transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] bg-white border border-[#172C37]/15 rounded-2xl overflow-hidden z-50 shadow-sm">
          <a
            href="tel:+6593651286"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-5 py-4 hover:bg-[#172C37]/5 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#172C37]/10 text-[#172C37]">
              <i className="ri-phone-line text-base" />
            </div>
            <div>
              <p className="text-xs text-[#172C37]/50 font-medium uppercase tracking-wide">Call Us</p>
              <p className="text-sm font-bold text-[#172C37]">+65 9365 1286</p>
            </div>
          </a>
          <div className="h-px bg-[#172C37]/8 mx-4" />
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-5 py-4 hover:bg-[#25D366]/5 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#25D366]/15 text-[#25D366]">
              <i className="ri-whatsapp-line text-base" />
            </div>
            <div>
              <p className="text-xs text-[#25D366]/70 font-medium uppercase tracking-wide">WhatsApp</p>
              <p className="text-sm font-bold text-[#172C37]">+65 9365 1286</p>
            </div>
          </a>
        </div>
      )}
    </div>
  );
}

export default function ShowflatSection() {
  const scrollToEnquiry = () => {
    document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* E-Brochure Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col-reverse gap-6 md:flex-row md:items-center md:gap-12">
            {/* Left Text */}
            <div className="flex-[1.5] flex flex-col gap-4">
              <h2
                className="text-2xl md:text-3xl font-bold text-[#172C37]"
                style={{ fontFamily: '"Cormorant Garamond", serif' }}
              >
                Get Your E-Brochure Today!
              </h2>
              <p className="text-[#172C37]/70 text-sm leading-relaxed">
                Familiarize yourself with the vicinity of Vela Bay — a life of luxury that most
                Singaporeans covet. Get a comprehensive overview of unit types, floor plans,
                facilities, and pricing.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={scrollToEnquiry}
                  className="bg-[#172C37] text-white font-bold px-6 py-3 rounded-full hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap text-sm"
                >
                  Request E-Brochure
                </button>
                <a
                  href="tel:+6593651286"
                  className="border border-[#172C37] text-[#172C37] font-bold px-6 py-3 rounded-full hover:bg-[#172C37]/5 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Call: +65 9365 1286
                </a>
              </div>
            </div>
            {/* Right Image */}
            <div className="w-full h-52 md:flex-1 md:h-64 rounded-2xl overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Vela Bay E-Brochure Preview"
                className="h-full w-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Showflat Viewing Section */}
      <section className="py-12 md:py-16 bg-[#F8F8F5]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-10">
            {/* Left: Main Image */}
            <div className="w-full h-56 md:col-span-2 md:h-96 rounded-2xl overflow-hidden">
              <img
                src={PROJECT_IMG}
                alt="Vela Bay Showflat Viewing Appointment"
                className="h-full w-full object-cover object-top"
                loading="lazy"
              />
            </div>

            {/* Right: Info */}
            <div className="md:col-span-1 flex flex-col justify-center gap-5">
              <div>
                <h2
                  className="text-xl md:text-2xl font-bold uppercase text-[#172C37] mb-2"
                  style={{ fontFamily: '"Cormorant Garamond", serif' }}
                >
                  ShowFlat Viewing
                </h2>
                <p className="text-sm text-[#172C37]/50 leading-relaxed">
                  Vela Bay Sales Gallery is strictly by appointment only.
                </p>
                <p className="text-sm text-[#172C37]/50 leading-relaxed mt-1">
                  Contact us now to receive your invitation to visit our showflat.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <HotlineDropdown />
                <button
                  onClick={scrollToEnquiry}
                  className="border border-[#172C37] text-[#172C37] font-bold px-6 py-3 rounded-full hover:bg-[#172C37]/5 transition-colors cursor-pointer whitespace-nowrap uppercase text-sm"
                >
                  Request Showflat Viewing
                </button>
              </div>

              <div className="bg-[#172C37]/5 rounded-xl p-4">
                <p className="text-xs text-[#172C37]/60 font-semibold uppercase tracking-wide mb-2">
                  Showflat Preview
                </p>
                <p className="text-xl md:text-2xl font-bold text-[#172C37]">11th April 2026</p>
                <p className="text-sm text-[#172C37]/60 mt-1">RSVP required — limited slots available</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
